import { useRef, useState } from 'react';
import type { DragEvent, ChangeEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import * as globalPrintingSettingsSelectors from '@makemydeal/dr-dash-store/selectors/globalPrintingSettingsSelectors';
import * as globalPrintingSettingsActionCreators from '@makemydeal/dr-dash-store/actions/globalPrintingSettingsActionCreators';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import { useFetchPrintingLogo } from '../../../hooks/useFetchPrintingLogo';
import { LOGO_OP_UPLOAD, LOGO_OP_DELETE } from './constants';
import { validateFileSync, getImageDimensions } from './utils';
import type { UseDealerLogoUploadReturn } from './types';
import type { DealerLogoMetadata } from '@makemydeal/dr-dash-types';

export const useDealerLogoUpload = (): UseDealerLogoUploadReturn => {
    const dispatch = useDispatch();

    // Load the dealer logo when this component is used, rather than on page initialization.
    useFetchPrintingLogo();

    const dealerId = useSelector(dealerSelectors.getDealerId);
    const bffConfig = useSelector(configSelectors.getServicesBff);
    const logoUrl = useSelector(globalPrintingSettingsSelectors.getGlobalPrintingSettingsLogoUrl);
    const isLoading = useSelector(globalPrintingSettingsSelectors.getGlobalPrintingSettingsIsLoading);
    const inlineError = useSelector(globalPrintingSettingsSelectors.getGlobalPrintingSettingsError);
    const uploadedFileName = useSelector(globalPrintingSettingsSelectors.getGlobalPrintingSettingsFileName);
    const uploadedFileSize = useSelector(globalPrintingSettingsSelectors.getGlobalPrintingSettingsFileSize);
    const imageWidth = useSelector(globalPrintingSettingsSelectors.getGlobalPrintingSettingsImageWidth);
    const imageHeight = useSelector(globalPrintingSettingsSelectors.getGlobalPrintingSettingsImageHeight);
    const lastOp = useSelector(globalPrintingSettingsSelectors.getGlobalPrintingSettingsLastOp);

    const [isDragActive, setIsDragActive] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const processFile = async (file: File) => {
        dispatch(globalPrintingSettingsActionCreators.clearPrintingLogoError());
        const syncError = validateFileSync(file);
        if (syncError) {
            dispatch(globalPrintingSettingsActionCreators.setLogoClientError(syncError));
            return;
        }
        const { width: imgWidth, height: imgHeight } = await getImageDimensions(file);
        const metadata: DealerLogoMetadata = {
            fileName: file.name,
            size: file.size,
            type: file.type,
            imageWidth: imgWidth,
            imageHeight: imgHeight
        };
        dispatch(globalPrintingSettingsActionCreators.uploadLogo(file, dealerId, metadata));
    };

    const handleBrowseClick = () => fileInputRef.current?.click();

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processFile(file);
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (file) processFile(file);
    };

    const handleDelete = () => {
        dispatch(globalPrintingSettingsActionCreators.clearPrintingLogoError());
        dispatch(globalPrintingSettingsActionCreators.deletePrintingLogo(dealerId, bffConfig));
    };

    return {
        logoUrl,
        isLoading,
        inlineError,
        uploadedFileName,
        uploadedFileSize,
        imageWidth,
        imageHeight,
        isDragActive,
        fileInputRef,
        handleBrowseClick,
        handleDragOver,
        handleDragEnter,
        handleDragLeave,
        handleDrop,
        handleFileChange,
        handleDelete,
        isUploadLoading: lastOp === LOGO_OP_UPLOAD && isLoading,
        isUploadLoaded: lastOp === LOGO_OP_UPLOAD && !isLoading && !!logoUrl && !inlineError,
        isUploadError: lastOp === LOGO_OP_UPLOAD && !isLoading && !!inlineError,
        isDeleteLoading: lastOp === LOGO_OP_DELETE && isLoading,
        isDeleteLoaded: lastOp === LOGO_OP_DELETE && !isLoading && !logoUrl && !inlineError,
        isDeleteError: lastOp === LOGO_OP_DELETE && !isLoading && !!inlineError
    };
};
